// Local draft model for the Build-Your-Card flow. A draft "card" mirrors the engine's
// Prediction but keeps the 1X2 base optional while the player is still choosing, and
// holds each multiplier boost (leg) with its own selection so the bet-slip projection
// can update live as boosts are tapped on and off.
//
// Football has no confidence ranking and no fixed pick count: the player taps into any
// subset of the slate, qualifies each card with a 1X2 base (worth CONFIG.COMBO.BASE_POINTS),
// and stacks up to CONFIG.COMBO.MAX_LEGS boosts drawn from the fixture's `legCatalog`.
// The "to win" is BASE × Π(boost multipliers); the downside is always exactly −BASE.
import {
  cardFloor,
  cardMultiplier,
  cardToWin,
  CONFIG,
  getMarket,
  type Entry,
  type Outcome,
  type Prediction,
  type PredictionLeg
} from '@h2h/shared';

/** Looks up the boost markets a fixture offers (its frozen `legCatalog`). */
export type LegCatalogLookup = (fixtureId: string) => readonly string[] | undefined;

export interface DraftLeg {
  /** The boost's market id (e.g. `'btts'`, `'exactScore'`, `'scorer'`). */
  marketId: string;
  /** The chosen option value: an option (`'YES'`, `'OVER'`, team-relative `'HOME'`),
   *  an `'H-A'` exact score, or a provider player id for the scorer boost. Empty
   *  until the player picks (markets with fixed options default-fill on add). */
  selection: string;
  /** Vanity display label (e.g. the scorer's name) — never used for grading. */
  label?: string;
}

export interface DraftCard {
  fixtureId: string;
  /** The 1X2 base (qualifier). null until the player taps a result. */
  outcome: Outcome | null;
  /** Stacked multiplier boosts (0..CONFIG.COMBO.MAX_LEGS), each a distinct market. */
  legs: DraftLeg[];
}

/** An empty card for a fixture the player is starting to build. */
export function emptyCard(fixtureId: string): DraftCard {
  return { fixtureId, outcome: null, legs: [] };
}

/** Does this draft have any meaningful content (a base or at least one boost)? Used
 *  to decide whether a tapped-then-abandoned fixture should be persisted at all. */
export function isCardStarted(c: DraftCard): boolean {
  return c.outcome != null || c.legs.length > 0;
}

/** A card is submittable once its 1X2 base is chosen and every stacked boost has a
 *  selection (boosts are all optional, but a half-filled boost is not). */
export function isCardComplete(c: DraftCard): boolean {
  return c.outcome != null && c.legs.every((l) => l.selection !== '');
}

export function hasLeg(c: DraftCard, marketId: string): boolean {
  return c.legs.some((l) => l.marketId === marketId);
}

export function legsAtMax(c: DraftCard): boolean {
  return c.legs.length >= CONFIG.COMBO.MAX_LEGS;
}

/** The card's combined multiplier (the ×N badge) — product of its boosts, each
 *  priced by its chosen outcome. */
export function draftMultiplier(c: DraftCard): number {
  return cardMultiplier(c.legs);
}

/** Format a multiplier for display: max 2 decimals, no trailing zeros (e.g. 7.181999… → "7.18", 2 → "2"). */
export function formatMultiplier(mult: number): string {
  return Number(mult.toFixed(2)).toString();
}

/** The bet-slip "to win": BASE × Π(boost multipliers), rounded. A bare base = BASE. */
export function draftToWin(c: DraftCard): number {
  return cardToWin(c.legs);
}

/** The "floor when right": what the card pays if the base lands but every boost
 *  misses. Equals BASE with no boosts and drops below it as boosts stack — the
 *  honest signal that adding boosts risks the safe base, not just adds upside. */
export function draftFloor(c: DraftCard): number {
  return cardFloor(c.legs);
}

/** The fixed downside shown on every card: a wrong base costs the whole base. */
export const CARD_AT_RISK = -CONFIG.COMBO.BASE_POINTS;

/**
 * Convert one draft card into an engine Prediction, or null if it isn't complete
 * (no base, or a boost left without a selection). Drops the transient label when it
 * adds nothing (the grader never reads it; we keep it only for scorer display).
 */
export function toCard(c: DraftCard): Prediction | null {
  if (c.outcome == null) return null;
  const legs: PredictionLeg[] = [];
  for (const l of c.legs) {
    if (l.selection === '') return null;
    const leg: PredictionLeg = { marketId: l.marketId, selection: l.selection };
    if (l.label) leg.label = l.label;
    legs.push(leg);
  }
  return { fixtureId: c.fixtureId, outcome: c.outcome, legs };
}

/**
 * Build the engine Predictions for an entry from the player's cards. Only STARTED
 * cards count (a tapped-then-cleared fixture is skipped); every started card must be
 * complete or the whole submit is blocked (returns null), so we never write a card
 * with a dangling boost. Football is per-fixture, so card order carries no meaning —
 * frozen (kicked-off) cards are reconstructed from the saved entry and re-sent
 * unchanged alongside the still-editable ones; the server's per-fixture freeze check
 * sees them byte-identical.
 */
export function toCards(cards: DraftCard[]): Prediction[] | null {
  const out: Prediction[] = [];
  for (const c of cards) {
    if (!isCardStarted(c)) continue;
    const pred = toCard(c);
    if (!pred) return null;
    out.push(pred);
  }
  return out;
}

/** Convert a saved entry back into editable draft cards (one per played fixture). */
export function draftFromEntry(entry: Entry): DraftCard[] {
  return entry.predictions.map((p) => ({
    fixtureId: p.fixtureId,
    outcome: p.outcome,
    legs: (p.legs ?? []).map((l) => ({
      marketId: l.marketId,
      selection: l.selection,
      ...(l.label ? { label: l.label } : {})
    }))
  }));
}

/** Does a boost market need an explicit choice from the player (vs. a yes/no that we
 *  could default), and which control? Thin wrapper over the shared market def so the
 *  builder UI can branch (ternary picker, exact-score stepper, scorer search). */
export function legKind(marketId: string): 'binary' | 'ternary' | 'band' | 'exact' | 'player' | undefined {
  return getMarket(marketId)?.kind;
}
